import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { API_ROUTE, ROUTE } from '@constants/index';
import { PlatformCheckerService } from '@services/platform-checker/platform-checker.service';
import { ErrorService } from '@services/error/error.service';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

declare const device: any;

@Injectable({
  providedIn: 'root',
})
export class MinVersionService {
  public isCordova = false;

  constructor(
    private readonly http: HttpClient,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly platformCheckerService: PlatformCheckerService
  ) {
    this.isCordova = this.platformCheckerService.isCordova();
  }

  public getMinVersion(): void {
    if (!this.platformCheckerService.isCordova()) return;

    const platform = (device?.platform ?? '').toString().toLowerCase();
    const isAndroid = platform === 'android';
    if (!isAndroid) return;

    const currentVersion = MinVersionService.getClientVersion();
    if (!currentVersion) return;

    const employeeId = MinVersionService.getEmployeeId();
    const url = API_ROUTE.MIN_VERSION;

    let params = new HttpParams().set('version', currentVersion);
    if (employeeId) {
      params = params.set('employeeId', employeeId);
    }

    this.http
      .get<any>(url, { params })
      .pipe(
        catchError(err => {
          this.sendMinVersionAudit({
            result: 'ERROR',
            employeeId,
            clientVersion: currentVersion,
            error: MinVersionService.safeError(err),
          });

          this.errorService.set('ERROR_DEFAULT', err);
          return EMPTY;
        })
      )
      .subscribe((data: any): void => {
        const shownScreen = this.checkMinimumVersionAndroid(
          currentVersion,
          data
        );

        this.sendMinVersionAudit({
          result: shownScreen ? 'KO' : 'OK',
          shownScreen,
          employeeId,
          clientVersion: currentVersion,
          minVersion: MinVersionService.extractMinVersionFromBack(data),
        });
      });
  }

  private checkMinimumVersionAndroid(
    currentVersion: string,
    data: any
  ): boolean {
    const minVersion = MinVersionService.extractMinVersionFromBack(data);

    if (
      !MinVersionService.isValidVersion(currentVersion) ||
      !MinVersionService.isValidVersion(minVersion)
    ) {
      return false;
    }

    if (MinVersionService.compareVersions(currentVersion, minVersion) < 0) {
      this.router.navigate([ROUTE.ERROR_VERSION]);
      return true;
    }

    return false;
  }

  private static getClientVersion(): string {
    const fromDevice = (
      device?.appVersion ??
      device?.versionName ??
      device?.version ??
      ''
    )
      .toString()
      .trim();

    if (fromDevice) return fromDevice;

    return '';
  }

  private static getEmployeeId(): string {
    const fromDevice = (device?.employeeId ?? '').toString().trim();
    if (fromDevice) return fromDevice;

    const fromLocalStorage = (localStorage.getItem('employeeId') ?? '')
      .toString()
      .trim();
    if (fromLocalStorage) return fromLocalStorage;

    return '';
  }

  private sendMinVersionAudit(payload: {
    result: 'OK' | 'KO' | 'ERROR';
    shownScreen?: boolean;
    employeeId?: string;
    clientVersion?: string;
    minVersion?: string;
    error?: string;
  }): void {
    try {
      const platform = (device?.platform ?? '').toString();
      const ua = (navigator?.userAgent ?? '').toString();

      const body = {
        ...payload,
        platform,
        ua,
      };

      this.http
        .post(API_ROUTE.MIN_VERSION_AUDIT, body)
        .pipe(catchError(() => EMPTY))
        .subscribe();
    } catch {
      // noop
    }
  }

  private static safeError(err: unknown): string {
    try {
      if (typeof err === 'string') return err;
      if (err && typeof err === 'object') {
        const anyErr = err as any;
        return (
          anyErr?.message ||
          anyErr?.statusText ||
          anyErr?.error?.message ||
          JSON.stringify(anyErr)
        );
      }
      return String(err);
    } catch {
      return 'unknown_error';
    }
  }

  private static extractMinVersionFromBack(data: any): string {
    const version = data?.APP_VERSION ?? data?.minVersion;
    return typeof version === 'string' ? version : '';
  }

  private static parseVersion(version: string): number[] | null {
    if (!version || typeof version !== 'string') return null;

    const cleaned = version
      .trim()
      .replace(/^v/i, '')
      .split('+')[0]
      .split('-')[0];

    const parts = cleaned.split('.').map(p => {
      const n = parseInt(p.replace(/[^0-9]/g, ''), 10);
      return Number.isFinite(n) ? n : 0;
    });

    if (!parts.length) return null;

    while (parts.length < 3) parts.push(0);
    if (parts.every(n => n === 0)) return null;

    return parts.slice(0, 3);
  }

  private static isValidVersion(version: string): boolean {
    return !!MinVersionService.parseVersion(version);
  }

  private static compareVersions(a: string, b: string): number {
    const av = MinVersionService.parseVersion(a) ?? [0, 0, 0];
    const bv = MinVersionService.parseVersion(b) ?? [0, 0, 0];

    for (let i = 0; i < 3; i += 1) {
      if (av[i] < bv[i]) return -1;
      if (av[i] > bv[i]) return 1;
    }

    return 0;
  }
}
